import { fade } from "@ui/Theme/utils/color-modifier";
import type { SelectedQuantity } from "../../useSelectedQuantities";
import { quantityColor } from "../../useSelectedQuantities";

type DrawZone<T = number> = (
  value: T,
  filterColor: string,
  lineColor: string,
) => Highcharts.SeriesZonesOptionsObject[];

const DASH_STYLE = "Dash";
const DASH_FADE_VALUE = 0.5;

const drawAnalysisFilterMinZones: DrawZone = (value, filterColor, lineColor) => [
  {
    value: value ?? undefined,
    dashStyle: DASH_STYLE,
    color: filterColor,
  },
  {
    color: lineColor,
  },
];

const drawAnalysisFilterMaxZones: DrawZone = (value, filterColor, lineColor) => [
  {
    value,
    color: lineColor,
  },
  {
    dashStyle: DASH_STYLE,
    color: filterColor,
  },
];

const drawAnalysisFilterMinMaxZones: DrawZone<number[]> = ([min, max], filterColor, lineColor) => [
  {
    value: min ?? undefined,
    dashStyle: DASH_STYLE,
    color: filterColor,
  },
  {
    value: max ?? undefined,
    color: lineColor,
  },
  {
    dashStyle: DASH_STYLE,
    color: filterColor,
  },
];

const emptyFilters: never[] = [];

/**
 * Create serie zoning to separate analysis that match filters.
 */
export function createAnalysisFilterZones(
  { quantity, colorIndex }: SelectedQuantity,
  activationFilters: {
    quantityId: string;
    min: number | null;
    max: number | null;
    maxStd: number | null;
  }[],
  selectedChartOption: "value" | "std" | undefined,
): Highcharts.SeriesZonesOptionsObject[] {
  const activationFilter = activationFilters.find(({ quantityId }) => quantityId === quantity.id);
  const quantityColorValue = quantityColor(colorIndex);
  const filterColor = fade(quantityColorValue, DASH_FADE_VALUE);
  const color = quantityColorValue;

  if (!activationFilter) return emptyFilters;

  if (selectedChartOption === "value") {
    if (activationFilter.min !== null && activationFilter.max === null)
      return drawAnalysisFilterMinZones(activationFilter.min, filterColor, color);
    if (activationFilter.min === null && activationFilter.max !== null)
      return drawAnalysisFilterMaxZones(activationFilter.max, filterColor, color);
    if (activationFilter.min !== null && activationFilter.max !== null)
      return drawAnalysisFilterMinMaxZones(
        [activationFilter.min, activationFilter.max],
        filterColor,
        color,
      );
  }

  if (selectedChartOption === "std") {
    if (activationFilter.maxStd !== null)
      return drawAnalysisFilterMaxZones(activationFilter.maxStd, filterColor, color);
  }

  return emptyFilters;
}
